import {
    Block,
    CHILD_OBJECT_INVALID,
    CHILD_TYPE_INVALID,
    type Change,
    FIRST_CHILD_TYPE_INVALID,
    type NodeType,
    PARENT_TYPE_INVALID,
    Schema,
    type SchemaRule,
} from '@gitbook/slate';

import { hasBlockConflict } from '../conflicts';
import * as paragraph from '../paragraph';
import {
    BLOCK_CODE,
    BLOCK_HINT,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_MATH,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_TABLE,
    LEAF_RICH_BLOCKS,
    LIST_BLOCKS,
} from '../types';
import { canInsertBlockIn } from '../utils';
import { isList } from './utils';

const ACCEPTED_FIRST_LIST_ITEM_BLOCKS = [
    ...LEAF_RICH_BLOCKS,
    BLOCK_CODE,
    BLOCK_HINT,
    BLOCK_QUOTE,
    BLOCK_MATH,

    // Terrible for UX, but we need to support this for now
    // because of GitBook-new
    BLOCK_TABLE,
];

/**
 * Validation for type of list
 */
function createListRule(schema: Pick<SchemaRule, 'data'>): SchemaRule {
    return {
        nodes: [{ types: [BLOCK_LIST_ITEM], min: 1 }],
        data: schema.data,
        normalize: Schema.createNormalize({
            [CHILD_TYPE_INVALID]: (change, context) => {
                // context.child is the "root" list, or the top-most parent list.
                if (Block.isBlock(context.child) && isList(context.child)) {
                    // If a list contains another list, unwrap the nested list. This "re-parents" the
                    // nested items under the "root" list.
                    return change.unwrapBlockByKey(context.child.key, context.child);
                } else {
                    // Otherwise wrap the child in a list item.
                    return change.wrapBlockByKey(context.child.key, BLOCK_LIST_ITEM);
                }
            },
        }),
    };
}

const listUnorderedRule = createListRule({
    data: {
        start: (value) => typeof value === 'undefined',
    },
});

const listOrderedRule = createListRule({
    data: {
        start: (value) => {
            if (typeof value === 'undefined') {
                return true;
            }
            if (typeof value !== 'number') {
                return false;
            }
            return value >= 1;
        },
    },
});

const listTasksRule = createListRule({
    data: {
        start: (value) => typeof value === 'undefined',
    },
});

/*
 * Schema to validate lists.
 */
export const schema = Schema.create({
    validations: [joinAdjacentLists],
    blocks: {
        [BLOCK_LIST_UL]: listUnorderedRule,
        [BLOCK_LIST_OL]: listOrderedRule,
        [BLOCK_LIST_TASKS]: listTasksRule,
        [BLOCK_LIST_ITEM]: {
            parent: { types: [BLOCK_LIST_OL, BLOCK_LIST_UL, BLOCK_LIST_TASKS] },
            nodes: [
                {
                    objects: ['block'],
                    types: [...ACCEPTED_FIRST_LIST_ITEM_BLOCKS, ...LIST_BLOCKS, BLOCK_IMAGES],
                    min: 1,
                },
            ],
            first: { types: ACCEPTED_FIRST_LIST_ITEM_BLOCKS },
            normalize: Schema.createNormalize({
                [PARENT_TYPE_INVALID]: (change, context) => {
                    if (
                        context.parent?.object === 'block' &&
                        context.parent.type === BLOCK_LIST_ITEM
                    ) {
                        // @ts-expect-error
                        return change.moveAfterNodeByKey(context.node.key, context.parent.key, {
                            normalize: false,
                        });
                    }

                    // @ts-expect-error
                    const listType = context.node.data.has('checked')
                        ? BLOCK_LIST_TASKS
                        : BLOCK_LIST_UL;
                    if (canInsertBlockIn(context.parent, listType)) {
                        return change.wrapBlockByKey(
                            // @ts-expect-error
                            context.node.key,
                            { type: listType },
                            { normalize: false }
                        );
                    } else {
                        return change.unwrapBlockByKey(
                            // @ts-expect-error
                            context.node.key,
                            {},
                            { normalize: false }
                        );
                    }
                },
                [CHILD_OBJECT_INVALID]: (change, context) =>
                    wrapChildrenInDefaultBlock(change, context.node),
                [FIRST_CHILD_TYPE_INVALID]: (change, context) => {
                    return change.insertNodeByKey(
                        // @ts-expect-error
                        context.node.key,
                        0,
                        paragraph.createParagraph()
                    );
                },
            }),
        },
    },
});

/*
 * Wraps all child of a node in the default block type.
 * Returns a change, for chaining purposes
 */
function wrapChildrenInDefaultBlock(change: Change, node: NodeType): Change {
    // @ts-expect-error
    change.wrapBlockByKey(node.nodes.first().key, BLOCK_PARAGRAPH, { normalize: false });

    // @ts-expect-error
    const wrapper = change.value.document.getDescendant(node.key).nodes.first();

    // Add in the remaining items
    // @ts-expect-error
    node.nodes
        .rest()
        .forEach((child, index: number) =>
            change.moveNodeByKey(child.key, wrapper.key, index + 1, { normalize: false })
        );

    return change;
}

/**
 * A rule that joins adjacent lists of the same type
 */
function joinAdjacentLists(node: NodeType): undefined | ((change: Change) => void) {
    if (node.object !== 'document' && node.object !== 'block') {
        return undefined;
    }

    const invalids = node.nodes.reduce<[Block, Block][]>((invalids, child: unknown, i) => {
        // Lists with conflicts are adjacent head/base that we don't want to merge
        if (!Block.isBlock(child) || !isList(child) || hasBlockConflict(child)) {
            return invalids;
        }
        const next = node.nodes.get(i + 1);

        if (
            !next ||
            !Block.isBlock(next) ||
            !isList(next) ||
            // Ensure lists of different types are not merged
            child.type !== next.type
        ) {
            return invalids;
        }

        const lastInvalid = invalids[invalids.length - 1];
        // If it's chained list, we merge it with the last one
        if (lastInvalid?.[1] === child) {
            invalids.push([lastInvalid[0], next]);
        } else {
            invalids.push([child, next]);
        }
        return invalids;
    }, []);

    if (invalids.length === 0) {
        return undefined;
    }

    // Join the list pairs
    // We join in reverse order, so that multiple lists folds onto the first one
    return (change) => {
        /**
         * The change operations in this function are applied in a linear fashion by first
         * accumulating all the changes to be made in a `changes` list and then applying
         * them by iterating over this. This was done to avoid an issue related to joining
         * of very large lists which could create a massive memory spike and cause the
         * process to crash. A possible explanation for this could be that while nested
         * looping over the Immutable List nodes and applying the operations respectively
         * can cause lots of readjustments to the underlying data structure which turns out
         * to be very expensive.
         *
         * So, instead of applying the changes in a nested loop, we first accumulate all
         * the changes to be made in a list and then apply them in a linear fashion. `normalize`
         * is disabled for these ops so there's no risk of something getting changed in between
         * we accumulate the changes & apply them.
         *
         * https://linear.app/gitbook-x/issue/SUP-507/git-sync-not-importing-content-from-gitlab-for-user
         */
        const changes: Array<[string, string, number] | string> = [];
        invalids.reverse().forEach((pair) => {
            const [first, second] = pair;
            const updatedSecond = change.value.document.getDescendant(second.key);
            if (!updatedSecond || !Block.isBlock(updatedSecond) || !isList(updatedSecond)) {
                return;
            }
            updatedSecond.nodes.forEach((secondNode, index) => {
                changes.push([secondNode.key, first.key, first.nodes.size + index]);
            });
            changes.push(second.key);
        });

        changes.forEach((ch) => {
            if (Array.isArray(ch)) {
                const [key, newKey, newIndex] = ch;
                change.moveNodeByKey(key, newKey, newIndex, { normalize: false });
            } else {
                change.removeNodeByKey(ch, { normalize: false });
            }
        });
    };
}
